"use client";

import type { Config, Field, Fields } from "@puckeditor/core";
import Box from "@mui/material/Box";
import type { ReactNode } from "react";
import { FormFunnelHeadingContext } from "@portal/app/_components/formFunnel/FormFunnelHeadingContext";
import { FormFunnelBannerImage } from "@portal/app/_components/formFunnel/FormFunnelBannerImage";
import { SANS_FONT_STACK } from "@portal/app/_components/theme/options/typography";
import { LandingPageBlock } from "../components/LandingPageBlock";
import {
  PAGE_BACKGROUND,
  PAGE_COLUMNS,
  PAGE_CONTENT_WIDTH,
  PAGE_DESKTOP_MEDIA,
  PAGE_SPACING,
} from "../constants";
import { PuckSurfaceTone, PuckSurfaceToneContext, usePuckSurfaceTone } from "./surfaceTone";
import type { PageBackground, PageMedia, ResolvedPageContentBlock } from "../types";
import { emptyLexicalContent } from "./designHelpers";
import { RichTextField } from "./components/RichTextField";
import { MediaField } from "./components/MediaField";
import { StatIconField } from "./components/StatIconField";
import {
  OptionalNumberField,
  OptionalNumberGroupField,
  type OptionalNumberGroupOptions,
  type OptionalNumberOptions,
} from "./components/OptionalNumberField";
import { PageText } from "./components/PageText";
import { PageStats } from "./components/PageStats";
import { PageHeading } from "./components/PageHeading";
import { PageImage, DEFAULT_IMAGE_FRAME } from "./components/PageImage";
import { PageVisibility, type PageVisibilityContext } from "./components/PageVisibility";
import { SupportingContent } from "./components/SupportingContent";
import { PageSupportingImage } from "./components/PageSupportingImage";
import { PageSupportingTestimonial } from "./components/PageTestimonial";
import { PageReassurance } from "./components/PageReassurance";
import type { PageChromeContext } from "./pageChromeTypes";
import { pageContentWebUrlSchema } from "../utils/validatePageContent";
import { isSafeFormFunnelContentHref } from "@portal/app/_common/lib/formFunnelContent";
import { featureGeometry, PUCK_BACKGROUND, PUCK_TABLET_MEDIA } from "./layoutGeometry";
import { createPageChromeBlocks } from "./pageChromeBlocks";
import type {
  PageOwnedFormOptions,
  PuckBlockProps,
  PuckRootProps,
  PuckSurfaceProps,
} from "./types";

const spacingOptions = [
  { label: "None", value: "none" },
  { label: "Small", value: "small" },
  { label: "Medium", value: "medium" },
  { label: "Large", value: "large" },
];
const backgroundField: Field = {
  type: "radio",
  label: "Background",
  options: [
    { label: "White", value: "white" },
    { label: "Soft", value: "muted" },
    { label: "Brand", value: "brand" },
    { label: "Transparent", value: "transparent" },
    { label: "Cream", value: "cream" },
  ],
};
const spacingField = (label: string): Field => ({ type: "radio", label, options: spacingOptions });
const spacingUnitField: Field = {
  type: "radio",
  label: "Spacing scale",
  options: [
    { label: "Fixed", value: "px" },
    { label: "Follow site text scale", value: "rem" },
  ],
};
const pageLength = (pixels: number, unit?: "px" | "rem") =>
  unit === "rem" ? `${pixels / 16}rem` : `${pixels}px`;
const surfaceFields: Fields<PuckSurfaceProps> = {
  background: backgroundField,
  padding: spacingField("Padding"),
  radius: {
    type: "radio",
    label: "Corners",
    options: [
      { label: "Square", value: "none" },
      { label: "Rounded", value: "rounded" },
    ],
  },
};
const surfaceDefaults: PuckSurfaceProps = { background: "white", padding: "none", radius: "none" };
const alignField: Field = {
  type: "radio",
  label: "Alignment",
  options: [
    { label: "Left", value: "left" },
    { label: "Center", value: "center" },
  ],
};
const bodySizeField: Field = {
  type: "radio",
  label: "Text size",
  options: [
    { label: "Small", value: "small" },
    { label: "Medium", value: "medium" },
  ],
};

function Surface({
  children,
  background,
  padding,
  radius,
  fill = false,
}: PuckSurfaceProps & {
  children: ReactNode | ((tone: PageBackground) => ReactNode);
  fill?: boolean;
}) {
  const tone = usePuckSurfaceTone(background);
  return (
    <PuckSurfaceToneContext.Provider value={tone}>
      <Box
        data-supporting-block
        sx={{
          minWidth: 0,
          height: fill ? "100%" : undefined,
          boxSizing: "border-box",
          bgcolor: PUCK_BACKGROUND[background],
          p: `${PAGE_SPACING[padding]}px`,
          borderRadius: radius === "rounded" ? "20px" : 0,
          overflowWrap: "anywhere",
        }}
      >
        {typeof children === "function" ? children(tone) : children}
      </Box>
    </PuckSurfaceToneContext.Provider>
  );
}

function Placeholder({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: 140,
        display: "grid",
        placeItems: "center",
        border: "1px dashed #9ca9be",
        borderRadius: "12px",
        p: 2,
        color: "#475467",
        bgcolor: "#f4f6fc",
      }}
    >
      {children}
    </Box>
  );
}

/** Reuses the actual landing-page visuals; Puck only owns composition and its editing UI. */
export function createLandingPagePuckConfig(
  context: {
    media: PageMedia[];
    form: ReactNode;
    renderForm?: (options: PageOwnedFormOptions) => ReactNode;
    progressPercent?: number;
    readOnly?: boolean;
    onMediaCreated?: (media: PageMedia) => void;
  } & PageVisibilityContext &
    Pick<PageChromeContext, "legalLinks" | "topOffset" | "inert" | "onFormFocus">
): Config<PuckBlockProps, PuckRootProps> {
  const media = new Map(context.media.map((item) => [item.id, item]));
  const mediaField: Field<string> = {
    type: "custom",
    label: "Image",
    render: ({ id, value, onChange, readOnly }) => (
      <MediaField
        id={id}
        value={value}
        onChange={onChange}
        media={[...media.values()]}
        readOnly={readOnly || context.readOnly}
        onCreated={(image) => {
          media.set(image.id, image);
          context.onMediaCreated?.(image);
        }}
      />
    ),
  };
  const optionalNumber = (options: OptionalNumberOptions): Field<number | undefined> => ({
    type: "custom",
    label: options.label,
    render: ({ id, value, onChange, readOnly }) => (
      <OptionalNumberField
        id={id}
        {...options}
        value={value}
        onChange={onChange}
        readOnly={readOnly || context.readOnly}
      />
    ),
  });
  const optionalNumbers = (label: string, fields: OptionalNumberGroupOptions): Field => ({
    type: "custom",
    label,
    render: ({ id, value, onChange, readOnly }) => (
      <OptionalNumberGroupField
        id={id}
        label={label}
        fields={fields}
        value={value}
        onChange={onChange}
        readOnly={readOnly || context.readOnly}
      />
    ),
  });
  const base = (props: PuckSurfaceProps & { id: string }) => ({
    blockKey: props.id,
    desktopRow: 1,
    rowSpan: 1 as const,
    background:
      props.background === "cream" || props.background === "transparent"
        ? ("white" as const)
        : props.background,
    padding: props.padding,
    radius: props.radius,
  });
  const leaf = (
    block: ResolvedPageContentBlock,
    surface: PuckSurfaceProps = block,
    options: {
      typography?: PuckBlockProps["RichText"]["typography"];
      appearance?: "standard" | "campaign";
    } = {}
  ) => (
    <Surface {...surface} fill={block.blockType === "image" && block.sizing === "fill"}>
      {(tone) =>
        block.blockType === "richText" ? (
          <PageText
            block={{ ...block, background: tone }}
            typography={options.typography}
            media={context.media}
          />
        ) : block.blockType === "stats" ? (
          <PageStats block={{ ...block, background: tone }} appearance={options.appearance} />
        ) : (
          <LandingPageBlock block={{ ...block, background: tone }} />
        )
      }
    </Surface>
  );
  return {
    categories: {
      layout: {
        title: "Layout",
        components: [
          "Section",
          "Columns",
          "Feature",
          "Grid",
          "Card",
          "SupportingContent",
          "Visibility",
          "Divider",
          "Spacer",
        ],
      },
      content: {
        title: "Content",
        components: [
          "Heading",
          "RichText",
          "Image",
          "Stats",
          "Testimonial",
          "Links",
          "Disclosure",
          "Reassurance",
        ],
      },
      form: { title: "Form", components: ["Form"] },
      page: { title: "Page", components: ["PageHeader", "LegalLinks"] },
    },
    root: {
      fields: { title: { type: "text", label: "Design title" } },
      defaultProps: { title: "Landing page" },
      render: ({ children }) => (
        <Box
          sx={{
            bgcolor: "#fff",
            minHeight: "100vh",
            color: "text.primary",
            fontFamily: SANS_FONT_STACK,
          }}
        >
          {children}
        </Box>
      ),
    },
    components: {
      Reassurance: {
        label: "Reassurance",
        fields: { text: { type: "text", label: "Text" } },
        defaultProps: { text: "Free matching · No commitment" },
        render: (props) => <PageReassurance text={props.text} />,
      },
      Visibility: {
        label: "Content visibility",
        fields: {
          content: { type: "slot", label: "Content" },
          when: {
            type: "select",
            label: "Show content",
            options: [
              { label: "Always", value: "always" },
              { label: "First question", value: "first-question" },
              { label: "Until success", value: "except-success" },
              { label: "Except loading and success", value: "supporting" },
              { label: "Hidden", value: "hidden" },
            ],
          },
        },
        defaultProps: { content: [], when: "always" },
        render: ({ content: Content, when, puck }) => (
          <PageVisibility
            when={when}
            phase={context.phase}
            isFirstQuestion={context.isFirstQuestion}
            readOnly={context.readOnly}
          >
            <Content
              minEmptyHeight={100}
              style={puck.isEditing ? undefined : { display: "contents" }}
            />
          </PageVisibility>
        ),
      },
      SupportingContent: {
        label: "Supporting content",
        fields: {
          content: { type: "slot", label: "Blocks", disallow: ["Section"] },
          title: { type: "text", label: "Title" },
          layout: {
            type: "radio",
            label: "Block layout",
            options: [
              { label: "Vertical", value: "vertical" },
              { label: "Wrapping row", value: "horizontal" },
              { label: "Grid", value: "grid" },
            ],
          },
          appearance: {
            type: "radio",
            label: "Appearance",
            options: [
              { label: "Footer", value: "footer" },
              { label: "Sidebar", value: "sidebar" },
            ],
          },
          desktopSidebar: {
            type: "radio",
            label: "Fit narrower desktop columns",
            options: [
              { label: "No", value: false },
              { label: "Yes", value: true },
            ],
          },
        },
        defaultProps: { content: [], title: "", layout: "vertical", appearance: "footer" },
        render: (props) => <SupportingContent {...props} />,
      },
      Section: {
        label: "Section",
        fields: {
          spacingUnit: spacingUnitField,
          breakpointProfile: {
            type: "radio",
            label: "Responsive breakpoints",
            options: [
              { label: "Landing page", value: "standard" },
              { label: "Form flow", value: "funnel" },
            ],
          },
          content: { type: "slot" },
          background: backgroundField,
          paddingY: spacingField("Vertical spacing"),
          mobilePaddingY: spacingField("Mobile vertical spacing"),
          contentWidth: {
            type: "select",
            label: "Content width",
            options: [
              { label: "Narrow", value: "narrow" },
              { label: "Standard", value: "standard" },
              { label: "Wide", value: "wide" },
              { label: "Full", value: "full" },
            ],
          },
          gap: spacingField("Block gap"),
          verticalPadding: optionalNumbers("Custom vertical spacing (px)", {
            mobile: { label: "Mobile", min: 0, max: 160 },
            tablet: { label: "Tablet", min: 0, max: 160 },
            desktop: { label: "Desktop", min: 0, max: 160 },
          }),
          maxWidthPx: optionalNumber({
            label: "Custom page width (px, including gutters)",
            min: 280,
            max: 1920,
          }),
          stackedMaxWidthPx: optionalNumber({
            label: "Stacked content width (px)",
            min: 280,
            max: 1920,
          }),
          wideGutters: optionalNumber({ label: "Wide-screen gutters (px)", min: 0, max: 96 }),
          gutters: optionalNumbers("Horizontal gutters (px)", {
            mobile: { label: "Mobile", min: 0, max: 96 },
            tablet: { label: "Tablet", min: 0, max: 96 },
            desktop: { label: "Desktop", min: 0, max: 96 },
          }),
        },
        defaultProps: {
          content: [],
          background: "muted",
          paddingY: "medium",
          mobilePaddingY: "medium",
          contentWidth: "wide",
          gap: "medium",
        },
        render: ({
          content: Content,
          background,
          paddingY,
          mobilePaddingY,
          contentWidth,
          gap,
          maxWidthPx,
          stackedMaxWidthPx,
          wideGutters,
          spacingUnit,
          gutters,
          verticalPadding,
          breakpointProfile,
        }) => (
          <PuckSurfaceTone background={background}>
            <Box
              component="section"
              sx={{
                bgcolor: PUCK_BACKGROUND[background],
                py: pageLength(
                  verticalPadding?.mobile ?? PAGE_SPACING[mobilePaddingY ?? paddingY],
                  spacingUnit
                ),
                ...(verticalPadding
                  ? {
                      [breakpointProfile === "funnel"
                        ? "@media (min-width: 600px)"
                        : PUCK_TABLET_MEDIA]: {
                        py: pageLength(
                          verticalPadding.tablet ??
                            verticalPadding.mobile ??
                            PAGE_SPACING[mobilePaddingY ?? paddingY],
                          spacingUnit
                        ),
                      },
                    }
                  : {}),
                [breakpointProfile === "funnel" ? "@media (min-width: 800px)" : PAGE_DESKTOP_MEDIA]:
                  {
                    py: pageLength(verticalPadding?.desktop ?? PAGE_SPACING[paddingY], spacingUnit),
                  },
                ...(!maxWidthPx && !gutters ? { px: { xs: "20px", md: "40px" } } : {}),
              }}
            >
              <Box
                sx={{
                  maxWidth: stackedMaxWidthPx ?? maxWidthPx ?? PAGE_CONTENT_WIDTH[contentWidth],
                  mx: "auto",
                  boxSizing: "border-box",
                  ...(gutters || maxWidthPx
                    ? {
                        px: pageLength(gutters?.mobile ?? 20, spacingUnit),
                        [breakpointProfile === "funnel"
                          ? "@media (min-width: 600px)"
                          : PUCK_TABLET_MEDIA]: {
                          px: pageLength(gutters?.tablet ?? 24, spacingUnit),
                        },
                        [breakpointProfile === "funnel"
                          ? "@media (min-width: 800px)"
                          : PAGE_DESKTOP_MEDIA]: {
                          px: pageLength(gutters?.desktop ?? 40, spacingUnit),
                          maxWidth: maxWidthPx ?? PAGE_CONTENT_WIDTH[contentWidth],
                        },
                      }
                    : {}),
                  ...(stackedMaxWidthPx && !gutters && !maxWidthPx
                    ? {
                        [breakpointProfile === "funnel"
                          ? "@media (min-width: 800px)"
                          : PAGE_DESKTOP_MEDIA]: { maxWidth: PAGE_CONTENT_WIDTH[contentWidth] },
                      }
                    : {}),
                  ...(wideGutters !== undefined
                    ? { "@media (min-width: 1100px)": { px: pageLength(wideGutters, spacingUnit) } }
                    : {}),
                }}
              >
                <Content
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: pageLength(PAGE_SPACING[gap], spacingUnit),
                  }}
                  minEmptyHeight={120}
                />
              </Box>
            </Box>
          </PuckSurfaceTone>
        ),
      },
      Columns: {
        label: "Columns",
        fields: {
          spacingUnit: spacingUnitField,
          left: { type: "slot", disallow: ["Section"] },
          right: { type: "slot", disallow: ["Section"] },
          columns: {
            type: "radio",
            label: "Column widths",
            options: [
              { label: "40 / 60", value: "40-60" },
              { label: "50 / 50", value: "50-50" },
              { label: "60 / 40", value: "60-40" },
            ],
          },
          gap: spacingField("Column gap"),
          blockGapPx: optionalNumber({
            label: "Space between blocks (px, optional)",
            min: 0,
            max: 96,
          }),
          mobileGap: spacingField("Mobile block gap"),
          mobileBlockGapPx: optionalNumber({
            label: "Mobile space between blocks (px, optional)",
            min: 0,
            max: 96,
          }),
          stackedMaxWidthPx: optionalNumber({
            label: "Maximum stacked width (px, optional)",
            min: 280,
            max: 1920,
          }),
          stackedGutters: optionalNumbers("Stacked gutters (px)", {
            mobile: { label: "Mobile", min: 0, max: 96 },
            tablet: { label: "Tablet", min: 0, max: 96 },
          }),
          leftWidthPx: optionalNumber({
            label: "Fixed left column width (px)",
            min: 160,
            max: 800,
          }),
          desktopInsets: optionalNumbers("Column padding (px, when side by side)", {
            leftStart: { label: "Left column: left", min: 0, max: 96 },
            leftEnd: { label: "Left column: right", min: 0, max: 96 },
            rightStart: { label: "Right column: left", min: 0, max: 96 },
            rightEnd: { label: "Right column: right", min: 0, max: 96 },
          }),
          wideInsets: optionalNumbers("Wide-screen column padding (px, 1100px+)", {
            leftStart: { label: "Left column: left", min: 0, max: 96 },
            leftEnd: { label: "Left column: right", min: 0, max: 96 },
            rightStart: { label: "Right column: left", min: 0, max: 96 },
            rightEnd: { label: "Right column: right", min: 0, max: 96 },
          }),
          wideGapPx: optionalNumber({
            label: "Wide-screen column gap (px, 1100px+)",
            min: 0,
            max: 96,
          }),
          stackAt: {
            type: "select",
            label: "Stack columns below",
            options: [
              { label: "Desktop (992px)", value: "desktop" },
              { label: "Tablet (768px)", value: "tablet" },
              { label: "Compact desktop (800px)", value: "compact-desktop" },
              { label: "Keep side by side", value: "never" },
            ],
          },
          mobileOrder: {
            type: "radio",
            label: "Mobile reading order",
            options: [
              { label: "Left first", value: "left-first" },
              { label: "Right first", value: "right-first" },
            ],
          },
        },
        defaultProps: {
          left: [],
          right: [],
          columns: "40-60",
          gap: "large",
          stackAt: "desktop",
          mobileOrder: "left-first",
          mobileGap: "medium",
        },
        render: ({
          left: Left,
          right: Right,
          columns,
          gap,
          stackAt = "desktop",
          mobileOrder = "left-first",
          mobileGap,
          leftWidthPx,
          desktopInsets,
          wideInsets,
          wideGapPx,
          blockGapPx,
          mobileBlockGapPx,
          stackedMaxWidthPx,
          stackedGutters,
          spacingUnit,
        }) => {
          const breakpoint =
            stackAt === "tablet"
              ? PUCK_TABLET_MEDIA
              : stackAt === "compact-desktop"
                ? "@media (min-width: 800px)"
                : PAGE_DESKTOP_MEDIA;
          const desktop = {
            ...(stackedMaxWidthPx || stackedGutters ? { maxWidth: "none", px: 0 } : {}),
            gridTemplateColumns: leftWidthPx
              ? `minmax(0, min(${leftWidthPx}px, 60%)) minmax(0, 1fr)`
              : PAGE_COLUMNS[columns],
            gridTemplateAreas: '"left right"',
            "--column-block-gap": pageLength(blockGapPx ?? PAGE_SPACING[gap], spacingUnit),
          };
          const slotStyle = {
            display: "flex",
            minWidth: 0,
            flexDirection: "column" as const,
            gap: "var(--column-block-gap)",
          };
          const left = (
            <Left
              className="landing-column-left"
              style={{ ...slotStyle, boxSizing: "border-box", gridArea: "left" }}
              minEmptyHeight={160}
            />
          );
          const right = (
            <Right
              className="landing-column-right"
              style={{ ...slotStyle, boxSizing: "border-box", gridArea: "right" }}
              minEmptyHeight={160}
            />
          );
          const insets = (value: typeof desktopInsets) =>
            value
              ? {
                  "& > .landing-column-left": {
                    paddingLeft: pageLength(
                      value.leftStart ?? desktopInsets?.leftStart ?? 0,
                      spacingUnit
                    ),
                    paddingRight: pageLength(
                      value.leftEnd ?? desktopInsets?.leftEnd ?? 0,
                      spacingUnit
                    ),
                  },
                  "& > .landing-column-right": {
                    paddingLeft: pageLength(
                      value.rightStart ?? desktopInsets?.rightStart ?? 0,
                      spacingUnit
                    ),
                    paddingRight: pageLength(
                      value.rightEnd ?? desktopInsets?.rightEnd ?? 0,
                      spacingUnit
                    ),
                  },
                }
              : {};
          Object.assign(desktop, insets(desktopInsets));
          return (
            <Box
              data-testid="landing-page-columns"
              sx={{
                display: "grid",
                minWidth: 0,
                ...((stackedMaxWidthPx || stackedGutters) && stackAt !== "never"
                  ? {
                      width: "100%",
                      boxSizing: "border-box",
                      maxWidth: stackedMaxWidthPx,
                      mx: "auto",
                      px: pageLength(stackedGutters?.mobile ?? 0, spacingUnit),
                      [stackAt === "compact-desktop"
                        ? "@media (min-width: 600px)"
                        : PUCK_TABLET_MEDIA]: {
                        px: pageLength(
                          stackedGutters?.tablet ?? stackedGutters?.mobile ?? 0,
                          spacingUnit
                        ),
                      },
                    }
                  : {}),
                gridTemplateColumns: "minmax(0, 1fr)",
                gridTemplateAreas:
                  mobileOrder === "right-first" ? '"right" "left"' : '"left" "right"',
                gap: pageLength(PAGE_SPACING[mobileGap ?? gap], spacingUnit),
                "--column-block-gap": pageLength(
                  mobileBlockGapPx ?? blockGapPx ?? PAGE_SPACING[mobileGap ?? gap],
                  spacingUnit
                ),
                ...(stackAt === "never"
                  ? { ...desktop, gap: pageLength(PAGE_SPACING[gap], spacingUnit) }
                  : {
                      [breakpoint]: { ...desktop, gap: pageLength(PAGE_SPACING[gap], spacingUnit) },
                    }),
                "@media (min-width: 1100px)": {
                  ...insets(wideInsets),
                  ...(wideGapPx === undefined ? {} : { gap: pageLength(wideGapPx, spacingUnit) }),
                },
              }}
            >
              {mobileOrder === "right-first" ? (
                <>
                  {right}
                  {left}
                </>
              ) : (
                <>
                  {left}
                  {right}
                </>
              )}
            </Box>
          );
        },
      },
      Feature: {
        label: "Feature layout",
        fields: {
          media: { type: "slot", label: "Media", disallow: ["Section"] },
          main: { type: "slot", label: "Main content", disallow: ["Section"] },
          mainGapPx: optionalNumber({ label: "Main content gap (px)", min: 0, max: 96 }),
          mobileMainGapPx: optionalNumber({
            label: "Mobile main content gap (px)",
            min: 0,
            max: 96,
          }),
          after: { type: "slot", label: "Supporting content", disallow: ["Section"] },
          mediaWidth: { type: "number", label: "Desktop media width (px)", min: 160, max: 800 },
          mediaSide: {
            type: "radio",
            label: "Desktop media position",
            options: [
              { label: "Left", value: "left" },
              { label: "Right", value: "right" },
            ],
          },
          gap: { type: "number", label: "Desktop column gap (px)", min: 0, max: 96 },
          rowGap: optionalNumber({ label: "Desktop row gap (px)", min: 0, max: 96 }),
          mobileGap: { type: "number", label: "Mobile gap (px)", min: 0, max: 96 },
          tabletGap: { type: "number", label: "Tablet gap (px)", min: 0, max: 96 },
          mobileOrder: {
            type: "radio",
            label: "Mobile order",
            options: [
              { label: "Main first", value: "main-first" },
              { label: "Media first", value: "media-first" },
            ],
          },
          tabletOrder: {
            type: "radio",
            label: "Tablet order",
            options: [
              { label: "Main first", value: "main-first" },
              { label: "Media first", value: "media-first" },
            ],
          },
        },
        defaultProps: {
          media: [],
          main: [],
          after: [],
          mediaWidth: 500,
          mediaSide: "left",
          gap: 48,
          rowGap: 28,
          mobileGap: 20,
          tabletGap: 28,
          mobileOrder: "main-first",
          tabletOrder: "media-first",
        },
        render: ({ media: Media, main: Main, after: After, ...props }) => {
          const main = (
            <Main
              style={{
                gridArea: "main",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                gap: "var(--feature-main-gap)",
              }}
              minEmptyHeight={120}
            />
          );
          const media = (
            <Media
              style={{
                gridArea: "media",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                gap: 24,
              }}
              minEmptyHeight={120}
            />
          );
          return (
            <Box
              sx={{
                ...featureGeometry(props),
                "--feature-main-gap": `${props.mobileMainGapPx ?? props.mainGapPx ?? 24}px`,
                [PUCK_TABLET_MEDIA]: {
                  ...featureGeometry(props)[PUCK_TABLET_MEDIA],
                  "--feature-main-gap": `${props.mainGapPx ?? 24}px`,
                },
              }}
            >
              {props.mobileOrder === "media-first" ? (
                <>
                  {media}
                  {main}
                </>
              ) : (
                <>
                  {main}
                  {media}
                </>
              )}
              <After
                style={{
                  gridArea: "after",
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 0,
                  gap: 24,
                }}
                minEmptyHeight={100}
              />
            </Box>
          );
        },
      },
      Grid: {
        label: "Grid",
        fields: {
          spacingUnit: spacingUnitField,
          items: { type: "slot", disallow: ["Section"] },
          breakpointProfile: {
            type: "radio",
            label: "Responsive breakpoints",
            options: [
              { label: "Landing page", value: "standard" },
              { label: "Form flow", value: "funnel" },
            ],
          },
          gapPx: optionalNumber({ label: "Item gap (px)", min: 0, max: 96 }),
          mobileGapPx: optionalNumber({ label: "Mobile item gap (px)", min: 0, max: 96 }),
          columns: {
            type: "radio",
            label: "Columns",
            options: [
              { label: "2", value: 2 },
              { label: "3", value: 3 },
              { label: "4", value: 4 },
            ],
          },
          gap: spacingField("Item gap"),
          mobileColumns: {
            type: "radio",
            label: "Mobile columns",
            options: [
              { label: "1", value: 1 },
              { label: "2", value: 2 },
            ],
          },
          tabletColumns: {
            type: "radio",
            label: "Tablet columns",
            options: [1, 2, 3, 4].map((value) => ({ label: String(value), value })),
          },
        },
        defaultProps: { items: [], columns: 3, gap: "medium", mobileColumns: 1, tabletColumns: 1 },
        render: ({
          items: Items,
          columns,
          gap,
          mobileColumns = 1,
          tabletColumns = 1,
          breakpointProfile,
          gapPx,
          mobileGapPx,
          spacingUnit,
        }) => (
          <Box
            data-supporting-block
            sx={{
              "& > div": {
                gridTemplateColumns: `repeat(${mobileColumns}, minmax(0, 1fr))`,
                gap: pageLength(mobileGapPx ?? gapPx ?? PAGE_SPACING[gap], spacingUnit),
                [breakpointProfile === "funnel" ? "@media (min-width: 600px)" : PUCK_TABLET_MEDIA]:
                  {
                    gridTemplateColumns: `repeat(${tabletColumns}, minmax(0, 1fr))`,
                  },
                [breakpointProfile === "funnel" ? "@media (min-width: 800px)" : PAGE_DESKTOP_MEDIA]:
                  {
                    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                    gap: pageLength(gapPx ?? PAGE_SPACING[gap], spacingUnit),
                  },
              },
            }}
          >
            <Items style={{ display: "grid" }} minEmptyHeight={140} />
          </Box>
        ),
      },
      Card: {
        label: "Card",
        fields: {
          content: { type: "slot", disallow: ["Section"] },
          ...surfaceFields,
          gap: spacingField("Block gap"),
          gapPx: optionalNumber({ label: "Space between blocks (px, optional)", min: 0, max: 96 }),
          mobileGapPx: optionalNumber({
            label: "Mobile space between blocks (px, optional)",
            min: 0,
            max: 96,
          }),
        },
        defaultProps: {
          content: [],
          background: "white",
          padding: "medium",
          radius: "rounded",
          gap: "medium",
        },
        render: ({ content: Content, ...props }) => (
          <Surface {...props}>
            <Box
              sx={{
                "--card-block-gap": `${props.mobileGapPx ?? props.gapPx ?? PAGE_SPACING[props.gap]}px`,
                [PUCK_TABLET_MEDIA]: {
                  "--card-block-gap": `${props.gapPx ?? PAGE_SPACING[props.gap]}px`,
                },
              }}
            >
              <Content
                style={{ display: "flex", flexDirection: "column", gap: "var(--card-block-gap)" }}
                minEmptyHeight={120}
              />
            </Box>
          </Surface>
        ),
      },
      Heading: {
        resolveFields: ({ props }, { fields }) => {
          return Object.fromEntries(
            Object.entries(fields).filter(
              ([key]) => !(key === "size" && props.typography && props.typography !== "standard")
            )
          ) as typeof fields;
        },
        label: "Heading",
        fields: {
          text: { type: "textarea", label: "Heading" },
          eyebrow: { type: "text", label: "Eyebrow" },
          description: { type: "textarea", label: "Description" },
          level: {
            type: "radio",
            label: "Heading level",
            options: [
              { label: "H1", value: 1 },
              { label: "H2", value: 2 },
              { label: "H3", value: 3 },
            ],
          },
          size: {
            type: "radio",
            label: "Size",
            options: [
              { label: "Small", value: "small" },
              { label: "Medium", value: "medium" },
              { label: "Large", value: "large" },
            ],
          },
          align: alignField,
          typography: {
            type: "select",
            label: "Typography",
            options: [
              { label: "Standard", value: "standard" },
              { label: "Campaign headline", value: "campaign" },
              { label: "Form introduction", value: "intro" },
              { label: "Supporting heading", value: "supporting" },
            ],
          },
          ...surfaceFields,
        },
        defaultProps: {
          ...surfaceDefaults,
          text: "Your heading",
          eyebrow: "",
          description: "",
          level: 2,
          size: "large",
          align: "left",
        },
        render: (props) => (
          <Surface {...props}>
            {(tone) => (
              <PageHeading
                typography={props.typography}
                block={{
                  ...base(props),
                  background: tone,
                  blockType: "heading",
                  level: props.level,
                  size: props.size,
                  align: props.align,
                  content: { source: "optional" },
                  resolvedContent: {
                    text: props.text,
                    eyebrow: props.eyebrow,
                    description: props.description,
                  },
                }}
              />
            )}
          </Surface>
        ),
      },
      RichText: {
        label: "Text",
        fields: {
          content: {
            type: "custom",
            label: "Text",
            render: ({ id, value, onChange, readOnly }) => (
              <RichTextField
                id={id}
                value={value}
                onChange={onChange}
                readOnly={readOnly || context.readOnly}
                media={context.media}
              />
            ),
          },
          typography: {
            type: "radio",
            label: "Typography",
            options: [
              { label: "Standard", value: "standard" },
              { label: "Body text", value: "body" },
              { label: "Footer", value: "footer" },
              { label: "Article", value: "article" },
              { label: "Fine print", value: "finePrint" },
            ],
          },
          size: bodySizeField,
          align: alignField,
          ...surfaceFields,
        },
        defaultProps: {
          ...surfaceDefaults,
          content: emptyLexicalContent("Add your text here."),
          size: "medium",
          align: "left",
        },
        render: (props) =>
          leaf(
            {
              ...base(props),
              blockType: "richText",
              size: props.size,
              align: props.align,
              content: { source: "optional" },
              resolvedContent: { content: props.content },
            },
            props,
            { typography: props.typography }
          ),
      },
      Image: {
        resolveFields: ({ props }, { fields }) => {
          return Object.fromEntries(
            Object.entries(fields).filter(([key]) =>
              props.sizing === "fixed" ? key !== "focus" : key !== "frame"
            )
          ) as typeof fields;
        },
        label: "Image",
        // Let a Fill image grow as a direct flex child of its containing column.
        inline: true,
        fields: {
          mediaId: mediaField,
          externalUrl: { type: "text", label: "External image URL (when no image is selected)" },
          alt: { type: "text", label: "Alternative text override" },
          href: { type: "text", label: "Image link" },
          width: optionalNumber({ label: "Content image width (px)", min: 1, max: 20_000 }),
          height: optionalNumber({ label: "Content image height (px)", min: 1, max: 20_000 }),
          maxHeight: optionalNumber({ label: "Maximum image height (px)", min: 1, max: 20_000 }),
          appearance: {
            type: "select",
            label: "Appearance",
            options: [
              { label: "Standard", value: "standard" },
              { label: "Content image", value: "content" },
              { label: "Gallery image", value: "gallery" },
              { label: "Banner", value: "banner" },
            ],
          },
          caption: { type: "text", label: "Caption" },
          sizing: {
            type: "select",
            label: "Image sizing",
            options: [
              { label: "Natural", value: "natural" },
              { label: "Landscape", value: "landscape" },
              { label: "Portrait", value: "portrait" },
              { label: "Tall frame", value: "tall" },
              { label: "Fill column", value: "fill" },
              { label: "Responsive fixed frame", value: "fixed" },
            ],
          },
          fit: {
            type: "radio",
            label: "Fit",
            options: [
              { label: "Cover", value: "cover" },
              { label: "Contain", value: "contain" },
            ],
          },
          focus: {
            type: "select",
            label: "Focus",
            options: ["center", "top", "bottom", "left", "right"].map((value) => ({
              label: value,
              value,
            })),
          },
          frame: optionalNumbers("Responsive frame", {
            mobileHeight: { label: "Mobile height (px)", min: 80, max: 1200 },
            mobileCropHeight: { label: "Mobile visible height (px, optional)", min: 80, max: 1200 },
            mobileCropUntil: {
              label: "Crop below",
              options: [
                { label: "Tablet (768px)", value: 768 },
                { label: "Compact desktop (800px)", value: 800 },
                { label: "Desktop (992px)", value: 992 },
              ],
            },
            tabletHeight: { label: "Tablet height (px)", min: 80, max: 1200 },
            desktopHeight: { label: "Desktop height (px)", min: 80, max: 1200 },
            focalX: { label: "Horizontal focus (%)", min: 0, max: 100 },
            focalY: { label: "Vertical focus (%)", min: 0, max: 100 },
            mobileFocalY: { label: "Mobile vertical focus (%)", min: 0, max: 100 },
          }),
          hideCaptionOnMobile: {
            type: "radio",
            label: "Caption on mobile",
            options: [
              { label: "Show", value: false },
              { label: "Hide", value: true },
            ],
          },
          captionPlacement: {
            type: "radio",
            label: "Caption",
            options: [
              { label: "Below", value: "below" },
              { label: "Overlay bubble", value: "overlay" },
            ],
          },
          ...surfaceFields,
        },
        defaultProps: {
          ...surfaceDefaults,
          mediaId: "",
          caption: "",
          sizing: "portrait",
          fit: "cover",
          focus: "center",
          captionPlacement: "below",
          radius: "rounded",
          frame: DEFAULT_IMAGE_FRAME,
          hideCaptionOnMobile: false,
        },
        render: (props) => {
          const selected = media.get(props.mediaId);
          const external = pageContentWebUrlSchema.safeParse(props.externalUrl);
          const source: PageMedia | undefined =
            selected ??
            (external.success ? { id: props.id, url: external.data, alt: "" } : undefined);
          const image = source
            ? {
                ...source,
                alt: props.alt ?? source.alt,
                width: props.width ?? source.width,
                height: props.height ?? source.height,
              }
            : undefined;
          const supporting = props.appearance === "content" || props.appearance === "gallery";
          const content = image ? (
            <Surface {...props} fill={props.sizing === "fill"}>
              {(tone) =>
                supporting ? (
                  <PageSupportingImage
                    image={image}
                    gallery={props.appearance === "gallery"}
                    href={props.href}
                    inert={context.inert ?? true}
                    maxHeight={props.maxHeight}
                  />
                ) : props.appearance === "banner" ? (
                  <FormFunnelBannerImage image={image} />
                ) : (
                  <Box
                    sx={{
                      height: props.sizing === "fill" ? "100%" : undefined,
                      ...(props.maxHeight ? { "& img": { maxHeight: props.maxHeight } } : {}),
                    }}
                  >
                    <PageImage
                      fixed={props.sizing === "fixed"}
                      frame={props.frame}
                      hideCaptionOnMobile={props.hideCaptionOnMobile}
                      block={{
                        ...base(props),
                        background: tone,
                        blockType: "image",
                        sizing: props.sizing === "fixed" ? "portrait" : props.sizing,
                        fit: props.fit,
                        focus: props.focus,
                        captionPlacement: props.captionPlacement,
                        content: { source: "optional" },
                        resolvedContent: { image, caption: props.caption },
                      }}
                    />
                  </Box>
                )
              }
            </Surface>
          ) : (
            <Placeholder>Choose or upload an image in the Image field.</Placeholder>
          );
          return (
            <div
              data-supporting-block
              ref={props.puck.dragRef}
              style={{
                minWidth: 0,
                flex: props.sizing === "fill" ? "1 0 auto" : undefined,
                alignSelf: props.appearance === "gallery" ? "center" : undefined,
              }}
            >
              {!supporting && props.href && isSafeFormFunnelContentHref(props.href) ? (
                <Box
                  component={context.inert === false ? "a" : "span"}
                  href={context.inert === false ? props.href : undefined}
                  sx={{ display: "block" }}
                >
                  {content}
                </Box>
              ) : (
                content
              )}
            </div>
          );
        },
      },
      Stats: {
        label: "Stats",
        fields: {
          eyebrow: { type: "text", label: "Eyebrow" },
          title: { type: "text", label: "Title" },
          items: {
            type: "array",
            label: "Stat items",
            getItemSummary: (item) => `${item.value || "Stat"} — ${item.label || "Label"}`,
            arrayFields: {
              value: { type: "text", label: "Value" },
              label: { type: "textarea", label: "Caption" },
              icon: {
                type: "custom",
                label: "Icon",
                render: ({ id, value, onChange, readOnly }) => (
                  <StatIconField
                    id={id}
                    value={value}
                    onChange={onChange}
                    readOnly={readOnly || context.readOnly}
                  />
                ),
              },
            },
            defaultItemProps: { value: "100", label: "Your statistic" },
          },
          sourceText: { type: "textarea", label: "Source text" },
          sourceUrl: { type: "text", label: "Source URL" },
          sourceLinkLabel: { type: "text", label: "Source link label" },
          size: bodySizeField,
          itemLayout: {
            type: "radio",
            label: "Item layout",
            options: [
              { label: "Label first", value: "label-first" },
              { label: "Icon left", value: "icon-left" },
            ],
          },
          appearance: {
            type: "radio",
            label: "Appearance",
            options: [
              { label: "Standard", value: "standard" },
              { label: "Campaign", value: "campaign" },
            ],
          },
          ...surfaceFields,
        },
        defaultProps: {
          ...surfaceDefaults,
          eyebrow: "",
          title: "Career outlook",
          items: [
            { value: "$45,750", label: "Median yearly pay", icon: "wage" },
            { value: "6%", label: "Projected job growth", icon: "growth" },
            { value: "44,700", label: "Annual job openings", icon: "openings" },
          ],
          sourceText: "",
          sourceUrl: "",
          sourceLinkLabel: "Source",
          size: "medium",
          itemLayout: "icon-left",
        },
        render: (props) =>
          leaf(
            {
              ...base(props),
              blockType: "stats",
              size: props.size,
              itemLayout: props.itemLayout,
              content: { source: "optional" },
              resolvedContent: {
                eyebrow: props.eyebrow,
                title: props.title,
                items: props.items,
                sourceText: props.sourceText,
                sourceUrl: props.sourceUrl,
                sourceLinkLabel: props.sourceLinkLabel,
              },
            },
            props,
            { appearance: props.appearance }
          ),
      },
      Testimonial: {
        label: "Testimonial",
        fields: {
          quote: { type: "textarea", label: "Quote" },
          name: { type: "text", label: "Name" },
          mediaId: mediaField,
          externalUrl: { type: "text", label: "External photo URL (when no image is selected)" },
          align: alignField,
          appearance: {
            type: "radio",
            label: "Appearance",
            options: [
              { label: "Standard", value: "standard" },
              { label: "Supporting card", value: "supporting" },
            ],
          },
          ...surfaceFields,
        },
        defaultProps: {
          ...surfaceDefaults,
          quote: "Share a student's experience.",
          name: "Student",
          mediaId: "",
          align: "left",
        },
        render: (props) => {
          const external = pageContentWebUrlSchema.safeParse(props.externalUrl);
          const photo =
            media.get(props.mediaId) ??
            (external.success
              ? { id: props.id, url: external.data, alt: props.name.trim() }
              : undefined);
          return props.appearance === "supporting" ? (
            <PageSupportingTestimonial name={props.name} quote={props.quote} photo={photo} />
          ) : (
            leaf(
              {
                ...base(props),
                blockType: "testimonial",
                align: props.align,
                content: { source: "optional" },
                resolvedContent: {
                  quote: props.quote,
                  name: props.name,
                  photo,
                },
              },
              props
            )
          );
        },
      },
      Form: {
        label: "Form flow",
        fields: {
          maxWidthPx: optionalNumber({
            label: "Form content maximum width (px)",
            min: 280,
            max: 1200,
          }),
          label: { type: "text", label: "Editor label" },
          stepLayout: {
            type: "radio",
            label: "Form spacing",
            options: [
              { label: "Inline", value: "inline" },
              { label: "Step", value: "step" },
              { label: "After a full-width banner", value: "after-banner" },
            ],
          },
          questionTypography: {
            type: "radio",
            label: "Question typography",
            options: [
              { label: "Display", value: "standard" },
              { label: "Body", value: "body" },
            ],
          },
          introTitle: { type: "textarea", label: "Introduction title (first question)" },
          introSubtitle: { type: "textarea", label: "Introduction subtitle (first question)" },
          bannerMediaId: {
            type: "custom",
            label: "Introduction banner (first question)",
            render: ({ id, value, onChange, readOnly }) => (
              <MediaField
                id={id}
                value={value ?? ""}
                onChange={onChange}
                media={[...media.values()]}
                readOnly={readOnly || context.readOnly}
                onCreated={(image) => {
                  media.set(image.id, image);
                  context.onMediaCreated?.(image);
                }}
              />
            ),
          },
          ...surfaceFields,
        },
        defaultProps: {
          background: "white",
          padding: "medium",
          radius: "rounded",
          label: "Find programs",
          introTitle: "",
          introSubtitle: "",
          bannerMediaId: "",
        },
        permissions: { duplicate: false },
        render: (props) => (
          <Surface {...props}>
            {(tone) => (
              <Box
                role="region"
                data-landing-page-form
                aria-label={props.label}
                sx={{
                  minWidth: 0,
                  ...(props.maxWidthPx === undefined
                    ? {}
                    : { maxWidth: props.maxWidthPx, mx: "auto" }),
                  // Funnel fields own their colors and require a light inner surface.
                  bgcolor:
                    (props.background === "transparent" || props.background === "cream") &&
                    tone !== "brand"
                      ? "transparent"
                      : PAGE_BACKGROUND.white,
                  color: "#151e39",
                  p: tone === "brand" ? `${PAGE_SPACING.medium}px` : 0,
                  borderRadius: props.radius === "rounded" ? "20px" : 0,
                }}
              >
                <FormFunnelHeadingContext.Provider value="h2">
                  {(context.renderForm
                    ? context.renderForm({
                        introTitle: props.introTitle,
                        introSubtitle: props.introSubtitle,
                        bannerMediaId: props.bannerMediaId,
                        questionTypography: props.questionTypography,
                        ...(props.stepLayout ? { stepLayout: props.stepLayout } : {}),
                      })
                    : context.form) || (
                    <Placeholder>
                      Select a Form flow on the landing page to preview it here.
                    </Placeholder>
                  )}
                </FormFunnelHeadingContext.Provider>
              </Box>
            )}
          </Surface>
        ),
      },
      ...createPageChromeBlocks({
        progressPercent: context.progressPercent,
        readOnly: context.readOnly,
        inert: context.inert ?? true,
        legalLinks: context.legalLinks,
        topOffset: context.topOffset,
        media: context.media,
        onFormFocus: context.onFormFocus,
      }),
    },
  };
}
